import React, { FC, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useCookies } from 'next-client-cookies';
import { useTheme } from 'next-themes';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import ModalPopUp from 'components/commercetools-ui/organisms/modal-pop-up';
import { trackMenuNavigation } from 'helpers/analytics/ga4Events';
import { DARK, LIGHT } from 'helpers/constants/events';
import { resolveTextConfiguration } from 'helpers/hooks/useResolvedLocalizedObject';
import { cleanString } from 'helpers/utils/util';
import { usePathname } from 'i18n/routing';
import { useTranslations } from 'use-intl';
import { useAccount } from 'frontastic';
import { useFeatures } from 'providers/feature-flag';
import { useMessagePort } from 'providers/message-channel';
import CountrySwitchModal from './country-switch-modal';
import { TabsPath } from '../../../account/types/types';
import { HeaderContentProps } from '../../types/gmsb-header';

export interface Props {
  contentConfig: HeaderContentProps;
}

const MyAccountDropDown: FC<Props> = ({ contentConfig }) => {
  const {
    myAccountIconLight,
    myAccountIconDark,
    myAccountText,
    accountDropdownIconDark,
    accountDropdownIconLight,
    ariaLabelMyAccount,
    orderStatusMob,
    addressBookMob,
    paymentMethodsMob,
    myAccountMob,
    myAccountIcon,
    addressBookIcon,
    orderStatusIcon,
    paymentMethodsIcon,
    googlePhotosLabel,
    googleIcon,
    leaveStoreLabel,
    stayOnPageLabel,
    returnGooglePhotosLabel,
    returnGooglePhotosDesc,
    ariaLabelAccountOptions,
    myPhotosLabel
  } = contentConfig;

  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { resolvedTheme = 'light' } = useTheme();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentHash = searchParams.get('hash');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { enableMyPhotos } = useFeatures();
  const isMyPhotosEnabled = enableMyPhotos();
  const { account } = useAccount();
  const canSwitchCountry = account?.custom?.fields?.canSwitchCountry === true;
  const [isCountrySwitchOpen, setIsCountrySwitchOpen] = useState(false);

  const translate = useTranslations();

  const getImageAlt = (icon: ImageProps | null, label: string | undefined) => {
    const title = icon?.title;

    if (!title || (Array.isArray(title) && title.length === 0)) {
      return label;
    }

    if (typeof title === 'object') {
      const resolvedTitle = resolveTextConfiguration(title as Record<string, any>, locale);
      return typeof resolvedTitle === 'string' ? resolvedTitle : label;
    }

    return title;
  };

  const dropdownLinks = [
    {
      label: myAccountMob,
      link: `/account?hash=${TabsPath.MY_ACCOUNT}`,
      hash: TabsPath.MY_ACCOUNT,
      icon: myAccountIcon,
      alt: getImageAlt(myAccountIcon, myAccountMob),
    },
    ...(isMyPhotosEnabled
      ? [
          {
            label: myPhotosLabel,
            link: `/account?hash=${TabsPath.MY_PHOTOS}`,
            hash: TabsPath.MY_PHOTOS,
            icon: null,
            alt: getImageAlt(null, myPhotosLabel),
          },
        ]
      : []),
    {
      label: orderStatusMob,
      link: `/account?hash=${TabsPath.ORDERS_STATUS}`,
      hash: TabsPath.ORDERS_STATUS,
      icon: orderStatusIcon,
      alt: getImageAlt(orderStatusIcon, orderStatusMob),
    },
    {
      label: addressBookMob,
      link: `/account?hash=${TabsPath.ADDRESS_BOOK}`,
      hash: TabsPath.ADDRESS_BOOK,
      icon: addressBookIcon,
      alt: getImageAlt(addressBookIcon, addressBookMob),
    },
    {
      label: paymentMethodsMob,
      link: `/account?hash=${TabsPath.PAYMENT_METHODS}`,
      hash: TabsPath.PAYMENT_METHODS,
      icon: paymentMethodsIcon,
      alt: getImageAlt(paymentMethodsIcon, paymentMethodsMob),
    },
  ];

  const isActive = (linkPath: string, linkHash: string | null) => {
    const normalizedPath = pathname.replace(/\/$/, '') || '/';
    const normalizedLinkPath = linkPath.replace(/\/$/, '') || '/';

    // Highlight My Account if on /account with no hash or my account hash
    if (normalizedPath === '/account' && normalizedLinkPath === '/account' && linkHash === TabsPath.MY_ACCOUNT) {
      if (!currentHash || currentHash === TabsPath.MY_ACCOUNT) return true;
    }

    const normalizedCurrentHash = currentHash || null;
    const normalizedLinkHash = linkHash ?? null;

    return normalizedPath === normalizedLinkPath && normalizedCurrentHash === normalizedLinkHash;
  };

  const { sendExitStore } = useMessagePort();

  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const handlePhotos = () => {
    setIsOpenModal(false);
    sendExitStore();
  };

  const handleStayOnPage = () => {
    setIsOpenModal(false);
  };

  const handleLastItemKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();

      setIsMenuOpen(false);
      document.getElementById('my-account-dropdown-button')?.focus();
    }
  };

  return (
    <div className="group relative">
      <div onMouseEnter={() => setIsMenuOpen(true)} onMouseLeave={() => setIsMenuOpen(false)}>
        <button
          type="button"
          id="my-account-dropdown-button"
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
          aria-controls="my-account-listbox"
          aria-label={`${myAccountText}`}
          onClick={() => setIsMenuOpen((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && !isMenuOpen) {
              e.preventDefault();
              setIsMenuOpen(true);
              setTimeout(() => {
                const firstItem = document.querySelector('#my-account-dropdown-menu a') as HTMLElement;
                firstItem?.focus();
              }, 50);
            }
          }}
          className="flex-0 relative flex cursor-pointer flex-row items-center border-none bg-transparent text-left md:mt-0"
        >
          {resolvedTheme == DARK && (
            <Image
              media={myAccountIconDark?.media}
              width={Number(myAccountIconDark?.media?.width || 24)}
              height={Number(myAccountIconDark?.media?.height || 24)}
              alt={ariaLabelMyAccount}
              data-ffid={`img-toggle-my-account-section-dark`}
              aria-hidden="true"
            />
          )}
          {resolvedTheme == LIGHT && (
            <Image
              media={myAccountIconLight?.media}
              width={Number(myAccountIconLight?.media?.width || 24)}
              height={Number(myAccountIconLight?.media?.height || 24)}
              alt={ariaLabelMyAccount}
              data-ffid={`img-toggle-my-account-section-light`}
              aria-hidden="true"
            />
          )}
          <span className="mx-4" data-ffid="lbl-myAccount" data-testid="lbl-myAccount" aria-hidden="true">
            {myAccountText}
          </span>
          <span>
            {resolvedTheme == DARK && (
              <Image
                data-ffid={`img-drk-dropdown`}
                media={accountDropdownIconDark?.media}
                width={Number(accountDropdownIconDark?.media?.width || 24)}
                height={Number(accountDropdownIconDark?.media?.height || 24)}
                aria-hidden="true"
              />
            )}
  
            {resolvedTheme == LIGHT && (
              <Image
                data-ffid={`img-lgt-dropdown`}
                media={accountDropdownIconLight?.media}
                width={Number(accountDropdownIconLight?.media?.width || 24)}
                height={Number(accountDropdownIconLight?.media?.height || 24)}
                alt={ariaLabelMyAccount}
                aria-hidden="true"
              />
            )}
          </span>
        </button>
          
        <div
          id="my-account-listbox"
          className={`absolute right-0 top-full z-[400] mt-0 w-240 animate-[appearDropdown_0.15s_ease-in-out] rounded-xl ${
            isMenuOpen ? 'block' : 'hidden'
          }`}
        >
          <div className="mt-20 rounded-[20px] bg-white p-16" style={{ boxShadow: `0px 0px 8px 0px #00000026` }}>
            <ul
              className="flex flex-col gap-8"
              aria-label={ariaLabelAccountOptions}
              role="menu"
              onKeyDown={(e) => {
                const focusableElements = document.querySelectorAll(
                  '#my-account-dropdown-menu a, #my-account-dropdown-menu button',
                );
                const activeEl = document.activeElement;
                const currentIndex = Array.from(focusableElements).indexOf(activeEl as Element);
              
                if (currentIndex === -1) return;
              
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  const nextLink = focusableElements[currentIndex + 1] as HTMLElement;
                  if (nextLink) nextLink.focus();
                  else (focusableElements[0] as HTMLElement).focus();
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  const prevLink = focusableElements[currentIndex - 1] as HTMLElement;
                  if (prevLink) prevLink.focus();
                  else (focusableElements[focusableElements.length - 1] as HTMLElement).focus();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setIsMenuOpen(false);
                  const triggerButton = document.getElementById('my-account-dropdown-button') as HTMLElement;
                  triggerButton?.focus();
                }
              }}
            >
              {dropdownLinks.map((item, index) => {
                const isCurrentActive = isActive(item.link.split('?')[0], item.hash);
                const itemLabel = item.label ?? '';
                const itemLabelSlug = cleanString(itemLabel);
              
                return (
                  <li key={`${item?.label}-${index}`} role="none">
                    <Link
                      title={itemLabel}
                      link={item.link}
                      className="group flex items-center gap-12 text-[#1f1f1f]"
                      onClick={() => {
                        let menuText = '';
                        if (item.hash === TabsPath.ORDERS_STATUS) menuText = 'Order History';
                        else if (item.hash === TabsPath.MY_PHOTOS) menuText = 'My Photos';
                        else if (item.hash === TabsPath.ADDRESS_BOOK) menuText = 'Address Book';
                        else if (item.hash === TabsPath.PAYMENT_METHODS) menuText = 'Payment Methods';
                        else if (item.hash === TabsPath.MY_ACCOUNT) menuText = 'My Account';
                      
                        if (menuText) {
                          trackMenuNavigation('Header My Account', menuText, item.link);
                        }
                      }}
                      data-ffid={`lnk-my-account-dropdown-${itemLabelSlug}`}
                      aria-current={isCurrentActive ? 'page' : undefined}
                      role="menuitem"
                    >
                      {item.icon?.media && (
                        <Image
                          media={item.icon.media}
                          width={24}
                          height={24}
                          alt={item.alt}
                          className="transition-transform group-hover:scale-110"
                          data-ffid={`icn-my-account-dropdown-${itemLabelSlug}`}
                        />
                      )}
                      <span
                        className={`whitespace-nowrap text-16 leading-normal ${
                          isCurrentActive ? 'font-bold' : 'font-normal'
                        }`}
                        data-ffid={`lbl-my-account-dropdown-${itemLabelSlug}`}
                      >
                        {item.label}
                      </span>
                    </Link>
                  </li>
                );
              })}
  
              {canSwitchCountry && (
                <li role="none">
                  <button
                    type="button"
                    className="group flex w-full items-center gap-12 text-[#1f1f1f]"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsCountrySwitchOpen(true);
                    }}
                    data-ffid="btn-my-account-dropdown-switch-country"
                    role="menuitem"
                  >
                    <span
                      className="whitespace-nowrap text-16 font-normal leading-normal"
                      data-ffid="lbl-my-account-dropdown-switch-country"
                    >
                      {translate('customer-support.country-switch-title')}
                    </span>
                  </button>
                </li>
              )}
  
              <li role="none" className="border-1 mt-8 flex border-t border-[#EBEBEB] pt-8">
                <button
                  data-ffid="btn-google-photo-image"
                  className="flex items-center gap-8 text-16 text-[#1f1f1f]"
                  onClick={() => setIsOpenModal(true)}
                  onKeyDown={handleLastItemKeyDown}
                  aria-label={googlePhotosLabel || 'Return to Google Photos'}
                >
                  {googleIcon?.media && (
                    <img
                      src={googleIcon?.media?.file}
                      alt={getImageAlt(googleIcon, googlePhotosLabel)}
                      className="h-auto w-20 object-contain"
                      data-ffid="google-photo-image"
                    />
                  )}
                  <span>{googlePhotosLabel}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <ModalPopUp
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        title={returnGooglePhotosLabel}
        messageDescription={returnGooglePhotosDesc}
        showCancelButton
        cancelText={leaveStoreLabel}
        onCancel={() => handlePhotos()}
        actionText={stayOnPageLabel}
        onAction={() => {
          handleStayOnPage();
        }}
      />

      <CountrySwitchModal
        isOpen={isCountrySwitchOpen}
        onClose={() => setIsCountrySwitchOpen(false)}
      />
    </div>
  );
};

export default MyAccountDropDown;
